import React, { useEffect, useState } from 'react';
import { useTalentos } from '../../../../application/talentos/hooks/useTalentos';
import ToolbarComponent from '../../../ui/section';
import { Row, Col } from '../../../ui/grid';
import Loader from '../../../ui/loader';
import Input from '../../../ui/input';
import DynamicTable from '../../../ui/table2';
import './statistics.css';

const DATE_PRESETS = [
    { id: '7d', label: 'Últimos 7 días', days: 7 },
    { id: '14d', label: 'Últimos 14 días', days: 14 },
    { id: '1m', label: 'Último mes', days: 30 },
    { id: '3m', label: 'Últimos 3 meses', days: 90 },
    { id: '6m', label: 'Últimos 6 meses', days: 180 },
    { id: '1y', label: 'Último año', days: 365 },
    { id: 'custom', label: 'Personalizado', days: null }
];

const getDateRangeFromPreset = (presetId) => {
    const preset = DATE_PRESETS.find(p => p.id === presetId);
    if (!preset || preset.id === 'custom') return null;
    
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - preset.days);
    
    return {
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0]
    };
};

const CreditCodesStatisticsPage = () => {
    const { creditCodes, getUserById } = useTalentos();
    const { getStatisticsByCreator, isLoading } = creditCodes;

    const [statistics, setStatistics] = useState([]);
    const [userDetails, setUserDetails] = useState({});
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState(() => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setMonth(startDate.getMonth() - 1);
        return {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0]
        };
    });
    const [selectedPreset, setSelectedPreset] = useState('1m');
    const [isCustomMode, setIsCustomMode] = useState(false);

    useEffect(() => {
        loadStatistics();
    }, [dateRange.startDate, dateRange.endDate]);

    useEffect(() => {
        // Cargar detalles de usuarios cuando cambian las estadísticas
        if (statistics.length > 0) {
            loadUserDetails();
        }
    }, [statistics]);

    const loadStatistics = async () => {
        setLoading(true);
        try {
            const filters = {
                startDate: dateRange.startDate,
                endDate: dateRange.endDate
            };
            const stats = await getStatisticsByCreator(filters);
            setStatistics(stats || []);
        } catch (error) {
            console.error('Error al cargar estadísticas:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadUserDetails = async () => {
        const uniqueUserIds = [...new Set(statistics.map(stat => stat.creatorId))];
        const details = {};

        await Promise.all(
            uniqueUserIds.map(async (userId) => {
                try {
                    const user = await getUserById(userId);
                    if (user) {
                        details[userId] = {
                            name: user.name || 'N/A',
                            email: user.email || 'N/A'
                        };
                    } else {
                        details[userId] = {
                            name: 'Usuario no encontrado',
                            email: 'N/A'
                        };
                    }
                } catch (error) {
                    console.error(`Error al cargar usuario ${userId}:`, error);
                    details[userId] = {
                        name: 'Error al cargar',
                        email: 'N/A'
                    };
                }
            })
        );

        setUserDetails(details);
    };

    const handleDateChange = (field, value) => {
        setDateRange(prev => ({
            ...prev,
            [field]: value
        }));
        if (selectedPreset !== 'custom') {
            setSelectedPreset('custom');
            setIsCustomMode(true);
        }
    };

    const handlePresetSelect = (presetId) => {
        setSelectedPreset(presetId);
        if (presetId === 'custom') {
            setIsCustomMode(true);
        } else {
            setIsCustomMode(false);
            const newRange = getDateRangeFromPreset(presetId);
            if (newRange) {
                setDateRange(newRange);
            }
        }
    };

    const handleReset = () => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setMonth(startDate.getMonth() - 1);
        setDateRange({
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0]
        });
        setSelectedPreset('1m');
        setIsCustomMode(false);
    };

    const formatDateRange = () => {
        if (!dateRange.startDate || !dateRange.endDate) return '';
        const start = new Date(dateRange.startDate);
        const end = new Date(dateRange.endDate);
        const options = { day: 'numeric', month: 'short', year: 'numeric' };
        return `${start.toLocaleDateString('es-ES', options)} - ${end.toLocaleDateString('es-ES', options)}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('es-ES', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const columns = [
        { key: 'user', label: 'Usuario', style: { width: '25%' } },
        { key: 'totalCodes', label: 'Total Códigos', style: { width: '12%' } },
        { key: 'activeCodes', label: 'Activos', style: { width: '10%' } },
        { key: 'expiredCodes', label: 'Expirados', style: { width: '10%' } },
        { key: 'disabledCodes', label: 'Desactivados', style: { width: '12%' } },
        { key: 'totalCredits', label: 'Total Créditos', style: { width: '12%' } },
        { key: 'firstCodeDate', label: 'Primer Código', style: { width: '12%' } },
        { key: 'lastCodeDate', label: 'Último Código', style: { width: '12%' } }
    ];

    const tableData = statistics.map(stat => {
        const user = userDetails[stat.creatorId] || { name: 'Cargando...', email: '' };
        return {
            ...stat,
            user: `${user.name}${user.email ? ` (${user.email})` : ''}`,
            totalCredits: stat.totalCredits || 0,
            firstCodeDate: formatDate(stat.firstCodeDate),
            lastCodeDate: formatDate(stat.lastCodeDate)
        };
    });

    const totalSummary = statistics.reduce((acc, stat) => ({
        totalCodes: acc.totalCodes + stat.totalCodes,
        activeCodes: acc.activeCodes + stat.activeCodes,
        expiredCodes: acc.expiredCodes + stat.expiredCodes,
        disabledCodes: acc.disabledCodes + stat.disabledCodes,
        totalCredits: acc.totalCredits + stat.totalCredits
    }), { totalCodes: 0, activeCodes: 0, expiredCodes: 0, disabledCodes: 0, totalCredits: 0 });

    if (loading || isLoading) {
        return (
            <ToolbarComponent label="Estadísticas de Códigos de Crédito" showBackButton={false}>
                <Loader text="Cargando estadísticas..." />
            </ToolbarComponent>
        );
    }

    return (
        <ToolbarComponent label="Estadísticas de Códigos de Crédito" showBackButton={false}>
            <div className="statistics-container">
                {/* Filtros de fecha */}
                <div className="date-filter-container">
                    <div className="date-filter-header">
                        <div className="date-filter-label">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2.66667H11.3333V2C11.3333 1.63181 11.0349 1.33333 10.6667 1.33333C10.2985 1.33333 10 1.63181 10 2V2.66667H6V2C6 1.63181 5.70152 1.33333 5.33333 1.33333C4.96514 1.33333 4.66667 1.63181 4.66667 2V2.66667H4C2.89543 2.66667 2 3.5621 2 4.66667V12C2 13.1046 2.89543 14 4 14H12C13.1046 14 14 13.1046 14 12V4.66667C14 3.5621 13.1046 2.66667 12 2.66667ZM12.6667 12C12.6667 12.3682 12.3682 12.6667 12 12.6667H4C3.63181 12.6667 3.33333 12.3682 3.33333 12V6.66667H12.6667V12Z" fill="currentColor"/>
                            </svg>
                            <span>Rango de fechas</span>
                        </div>
                        <div className="date-filter-actions">
                            {dateRange.startDate && dateRange.endDate && (
                                <div className="date-range-display">
                                    {formatDateRange()}
                                </div>
                            )}
                            <button 
                                className="date-reset-btn"
                                onClick={handleReset}
                                title="Restablecer filtros"
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8 2.66667C5.05448 2.66667 2.66667 5.05448 2.66667 8C2.66667 10.9455 5.05448 13.3333 8 13.3333C10.9455 13.3333 13.3333 10.9455 13.3333 8C13.3333 5.05448 10.9455 2.66667 8 2.66667ZM8 12C6.15905 12 4.66667 10.5076 4.66667 8.66667C4.66667 6.82571 6.15905 5.33333 8 5.33333C9.84095 5.33333 11.3333 6.82571 11.3333 8.66667C11.3333 10.5076 9.84095 12 8 12Z" fill="currentColor"/>
                                    <path d="M6.66667 6.66667L5.33333 8L6.66667 9.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M9.33333 6.66667L10.6667 8L9.33333 9.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                <span>Resetear</span>
                            </button>
                        </div>
                    </div>
                    
                    <div className="date-presets">
                        {DATE_PRESETS.map((preset) => (
                            <button
                                key={preset.id}
                                className={`date-preset-btn ${selectedPreset === preset.id ? 'active' : ''}`}
                                onClick={() => handlePresetSelect(preset.id)}
                            >
                                {preset.label}
                            </button>
                        ))}
                    </div>
                    
                    <div className={`date-custom-container ${isCustomMode ? 'expanded' : ''}`}>
                        <div className="date-custom-inputs">
                            <div className="date-input-wrapper">
                                <Input
                                    label="Fecha inicio"
                                    type="date"
                                    value={dateRange.startDate}
                                    onChange={(value) => handleDateChange('startDate', value)}
                                />
                            </div>
                            <div className="date-input-separator">
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                            <div className="date-input-wrapper">
                                <Input
                                    label="Fecha fin"
                                    type="date"
                                    value={dateRange.endDate}
                                    onChange={(value) => handleDateChange('endDate', value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Resumen */}
                <div className="statistics-section">
                    <h2 className="statistics-section-title">Resumen General</h2>
                    <div className="kpi-grid">
                        <div className="kpi-card">
                            <div className="kpi-card-label">Usuarios que generaron códigos</div>
                            <div className="kpi-card-value">{statistics.length}</div>
                        </div>
                        <div className="kpi-card">
                            <div className="kpi-card-label">Total de Códigos</div>
                            <div className="kpi-card-value">{totalSummary.totalCodes}</div>
                            <div className="kpi-card-subtitle">
                                {totalSummary.activeCodes} activos · {totalSummary.expiredCodes} expirados · {totalSummary.disabledCodes} desactivados
                            </div>
                        </div>
                        <div className="kpi-card">
                            <div className="kpi-card-label">Total de Créditos</div>
                            <div className="kpi-card-value">{totalSummary.totalCredits}</div>
                        </div>
                    </div>
                </div>

                <hr className="divider" />

                {/* Tabla de estadísticas */}
                <div className="statistics-section">
                    <h2 className="statistics-section-title">Estadísticas por Usuario</h2>
                    {tableData.length > 0 ? (
                        <DynamicTable
                            headers={columns}
                            data={tableData}
                            fullPage={false}
                            showLabel={false}
                            emptyText="No hay estadísticas disponibles"
                        />
                    ) : (
                        <div style={{ 
                            padding: '40px', 
                            textAlign: 'center', 
                            color: '#6B7280',
                            background: '#F9FAFB',
                            borderRadius: '8px'
                        }}>
                            No hay códigos generados en el rango de fechas seleccionado.
                        </div>
                    )}
                </div>
            </div>
        </ToolbarComponent>
    );
};

export default CreditCodesStatisticsPage;

